"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Items in the visitor's trip, for the navigation badges. Read in the browser
 * from /api/cart/count so the pages themselves stay cacheable (see that route).
 *
 * Re-read on every page change, which is when a trip changes (adding an item
 * goes through a page), and whenever the tab regains focus. The desktop and
 * phone navigations both use this; one request per page serves both.
 */
let inflight: { path: string; promise: Promise<number> } | null = null;

function fetchCount(path: string): Promise<number> {
  if (inflight?.path === path) return inflight.promise;
  const promise = fetch("/api/cart/count", { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : { count: 0 }))
    .then((d: { count?: number }) => (typeof d.count === "number" ? d.count : 0))
    .catch(() => 0);
  inflight = { path, promise };
  return promise;
}

export function useCartCount(): number {
  const pathname = usePathname() ?? "/";
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void fetchCount(pathname).then((n) => {
      if (!cancelled) setCount(n);
    });
    const onFocus = () => {
      inflight = null;
      void fetchCount(pathname).then((n) => {
        if (!cancelled) setCount(n);
      });
    };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [pathname]);

  return count;
}
